import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ChevronDown, ChevronRight, CalendarDays, Users, Workflow, FileText, AlertTriangle, Lightbulb, ExternalLink } from 'lucide-react';
import PageLayout from '@/components/layout/PageLayout';
import NotFoundPage from '@/pages/NotFoundPage';
import SolutionFlag from '@/components/solutions/SolutionFlag';
import SolutionGuideDownload from '@/components/solutions/SolutionGuideDownload';
import { getSolution, getSolutionCountry, solutionCountries, solutionPath, loadCountryDetail, fill } from '@/data/solutions/registry';
import { iconFor } from '@/data/solutions/icons';
const ARROW = '→';
const TaxeraLogo = '/taxera-logo-transparent.png';
export const statusClasses = (status) => {
  if (status === 'upcoming') return 'bg-amber-50 text-amber-800 border-amber-200';
  if (status === 'voluntary') return 'bg-slate-100 text-slate-700 border-slate-200';
  return 'bg-blue-50 text-[#0052A3] border-blue-200';
};
const FaqItem = ({ q, a, index, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-slate-200 rounded-xl bg-white overflow-hidden" data-testid={`sol-country-faq-${index}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-slate-50 transition-colors"
        data-testid={`sol-country-faq-toggle-${index}`}
      >
        <span className="text-sm sm:text-base font-semibold text-slate-900">{q}</span>
        <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <p className="px-5 pb-5 text-sm sm:text-base text-slate-600 leading-relaxed">{a}</p>}
    </div>
  );
};
const splitLabel = (text) => {
  const i = text.indexOf(': ');
  if (i === -1) return { label: null, body: text };
  return { label: text.slice(0, i), body: text.slice(i + 2) };
};
// A FAQ answer is built from one or more fields of the country entry.
const fieldText = (c, key) => {
  const v = c[key];
  if (v === undefined || v === null) return '';
  if (key === 'keyDates') return v.map((d) => `${d.date}: ${d.event}.`).join(' ');
  if (key === 'process') return String(v).replace(/ -> /g, ` ${ARROW} `);
  if (key === 'officialSources') {
    return v.length
      ? `The official sources are: ${v.map((o) => o.label).join('; ')}. They are linked at the bottom of this page. Always check them for the latest version of the rules.`
      : `Check the website of the tax authority in ${c.name} for the latest version of the rules.`;
  }
  if (Array.isArray(v)) return v.join(' ');
  return String(v);
};
export const buildFaqs = (s, c) =>
  s.country.faqs
    .map((f) => ({
      q: fill(f.q, { name: c.name, short: c.shortName, product: s.name }),
      a: f.a.map((k) => fieldText(c, k)).filter(Boolean).join('. ').replace(/\.\./g, '.'),
    }))
    .filter((f) => f.a);
export const SolutionCountryContent = ({ s, c, print = false }) => {
  const ui = s.country;
  const vars = { name: c.name, short: c.shortName, product: s.name };
  const faqs = buildFaqs(s, c);
  const flow = c.dataFlow ? c.dataFlow.split(' -> ') : [];
  const path = solutionPath(s.slug);
  const others = solutionCountries(s.slug).filter((o) => o.slug !== c.slug && o.region === c.region);
  const heading = fill(c.shortName ? ui.headingShort : ui.headingPlain, vars);
  const glance = c.glance || [];
  return (
    <div data-testid={`sol-country-page-${c.slug}`} className={print ? 'sol-print' : undefined}>
      {print && (
        <div className="max-w-6xl mx-auto px-6 pt-6 border-b border-slate-200 pb-3">
          <div className="flex items-center justify-between">
            <a href="https://taxera.tech" aria-label="Taxera">
              <img src={TaxeraLogo} alt="Taxera" style={{ height: '34px', width: 'auto' }} />
            </a>
            <div className="text-right text-xs text-slate-500">
              <div className="font-semibold tracking-wide text-slate-700">{fill(ui.printTitle, vars)}</div>
              <a href={`https://taxera.tech${path}/${c.slug}`} className="text-[#0066CC]">taxera.tech{path}/{c.slug}</a>
            </div>
          </div>
          <p className="mt-3 text-[11px] leading-snug text-slate-500">
            For information only. This guide is not tax, legal or accounting advice. Rules change, so please do your own research and check the official sources before you act.
          </p>
        </div>
      )}
      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-10 pb-14">
        {!print && (
          <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-xs text-slate-400 mb-8" data-testid="sol-country-breadcrumb">
            <Link to="/" className="hover:text-slate-700">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to={path} className="hover:text-slate-700">{s.name}</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-slate-600">{c.name}</span>
          </nav>
        )}
        <motion.div initial={print ? false : { opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <SolutionFlag code={c.code} name={c.name} className="h-7 w-7" />
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#14A3B8]">{fill(ui.eyebrow, vars)}</span>
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${statusClasses(c.status)}`} data-testid="sol-country-status">
              {c.statusLabel}
            </span>
            <span className="text-xs text-slate-400">Last reviewed {c.reviewed}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.1] max-w-4xl">{heading}</h1>
          <p className="mt-6 text-lg text-slate-600 max-w-3xl leading-relaxed">{c.summary}</p>
          <p className="mt-6 text-sm text-slate-500 max-w-3xl leading-relaxed" data-testid="sol-country-disclaimer">
            General information only, not tax advice. Rules change, so check the official sources at the bottom of this page before you act.
          </p>
          {!print && (
            <div className="mt-8" data-testid="sol-country-download">
              <SolutionGuideDownload s={s} c={c} />
            </div>
          )}
        </motion.div>
      </section>
      {/* Key facts */}
      <section className="bg-slate-50 border-y border-slate-100" data-testid="sol-country-facts">
        <div className="max-w-6xl mx-auto px-6 py-14">
          <div className="grid md:grid-cols-3 gap-5">
            <div className="rounded-2xl bg-white border border-slate-200 p-6">
              <FileText className="w-5 h-5 text-[#0066CC] mb-3" strokeWidth={1.5} />
              <h2 className="text-sm font-bold text-slate-900 mb-1">{ui.mandateLabel}</h2>
              <p className="text-sm text-slate-600 leading-relaxed">{c.mandateName}</p>
            </div>
            <div className="rounded-2xl bg-white border border-slate-200 p-6">
              <Users className="w-5 h-5 text-[#0066CC] mb-3" strokeWidth={1.5} />
              <h2 className="text-sm font-bold text-slate-900 mb-1">{ui.whoLabel}</h2>
              <p className="text-sm text-slate-600 leading-relaxed">{c.inScope}</p>
            </div>
            <div className="rounded-2xl bg-white border border-slate-200 p-6">
              <Workflow className="w-5 h-5 text-[#0066CC] mb-3" strokeWidth={1.5} />
              <h2 className="text-sm font-bold text-slate-900 mb-1">{ui.howLabel}</h2>
              <p className="text-sm text-slate-600 leading-relaxed">{String(c.process).replace(/ -> /g, ` ${ARROW} `)}</p>
            </div>
          </div>
          {glance.length > 0 && (
            <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="sol-country-glance">
              {glance.map((g) => {
                const Icon = iconFor(g.icon);
                return (
                  <div key={g.label} className="rounded-2xl bg-white border border-slate-200 p-6">
                    <Icon className="w-5 h-5 text-[#0066CC] mb-3" strokeWidth={1.5} />
                    <h2 className="text-sm font-bold text-slate-900 mb-1">{g.label}</h2>
                    <p className="text-sm text-slate-600 leading-relaxed">{g.value}</p>
                    {g.note && <p className="mt-2 text-xs text-slate-500 leading-relaxed">{g.note}</p>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
      {/* Key dates */}
      <section className="max-w-4xl mx-auto px-6 py-14" data-testid="sol-country-dates">
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-8 flex items-center gap-3">
          <CalendarDays className="w-6 h-6 text-[#14A3B8]" strokeWidth={1.5} /> {fill(ui.datesHeading, vars)}
        </h2>
        <ol className="relative border-l-2 border-slate-200 ml-2 space-y-6">
          {c.keyDates.map((d, i) => (
            <li key={`${d.date}-${i}`} className="pl-6 relative" data-testid={`sol-country-date-${i}`}>
              <span className="absolute -left-[7px] top-1.5 w-3 h-3 rounded-full bg-[#0066CC]" />
              <div className="text-sm font-bold text-[#0066CC]">{d.date}</div>
              <div className="text-slate-700">{d.event}</div>
            </li>
          ))}
        </ol>
      </section>
      {/* What it means for your systems */}
      <section className="bg-slate-50 border-y border-slate-100" data-testid="sol-country-systems">
        <div className="max-w-6xl mx-auto px-6 py-14 grid lg:grid-cols-2 gap-10">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">{ui.leftHeading}</h2>
            <p className="text-slate-600 leading-relaxed">{c.businessProcess}</p>
          </div>
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">{ui.rightHeading}</h2>
            <p className="text-slate-600 leading-relaxed">{c.systemImplications}</p>
          </div>
        </div>
      </section>
      {/* Data flow */}
      {flow.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 py-14" data-testid="sol-country-flow">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-8">{ui.flowHeading}</h2>
          <div className="flex flex-wrap items-stretch gap-3">
            {flow.map((step, i) => (
              <div key={`${step}-${i}`} className="flex items-center gap-3">
                <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-800">{step}</div>
                {i < flow.length - 1 && <ArrowRight className="w-4 h-4 text-slate-300 shrink-0" />}
              </div>
            ))}
          </div>
        </section>
      )}
      {/* Insights and pitfalls */}
      <section className="bg-slate-50 border-y border-slate-100" data-testid="sol-country-insights">
        <div className="max-w-6xl mx-auto px-6 py-14 grid lg:grid-cols-2 gap-8">
          <div>
            <h2 className="text-xl font-bold text-slate-900 mb-5 flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-[#14A3B8]" strokeWidth={1.5} /> Practical insights
            </h2>
            <ul className="space-y-4">
              {c.practicalInsights.map((t, i) => (
                <li key={i} className="text-sm text-slate-600 leading-relaxed rounded-xl bg-white border border-slate-200 p-4">{t}</li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900 mb-5 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" strokeWidth={1.5} /> Common pitfalls
            </h2>
            <ul className="space-y-4">
              {c.commonPitfalls.map((t, i) => (
                <li key={i} className="text-sm text-slate-600 leading-relaxed rounded-xl bg-white border border-amber-100 p-4">{t}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>
      {/* Connected obligations */}
      {c.dependencies && c.dependencies.length > 0 && (
        <section className="max-w-4xl mx-auto px-6 py-14" data-testid="sol-country-dependencies">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-6">{fill(ui.linkedHeading, vars)}</h2>
          <div className="space-y-3">
            {c.dependencies.map((d, i) => {
              const { label, body } = splitLabel(d);
              return (
                <div key={i} className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600 leading-relaxed">
                  {label && <span className="font-semibold text-slate-900">{label}: </span>}
                  {body}
                </div>
              );
            })}
          </div>
        </section>
      )}
      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-6 py-16" data-testid="sol-country-faq">
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-8">{fill(ui.faqHeading, vars)}</h2>
        <div className="space-y-3">
          {faqs.map((f, i) => (
            <FaqItem key={f.q} q={f.q} a={f.a} index={i} defaultOpen={print} />
          ))}
        </div>
      </section>
      {/* Sources + other countries */}
      <section className="max-w-6xl mx-auto px-6 pb-20" data-testid="sol-country-sources">
        {c.officialSources && c.officialSources.length > 0 && (
          <div className="mb-10">
            <h2 className="text-lg font-bold text-slate-900 mb-3">Official sources</h2>
            <ul className="space-y-2">
              {c.officialSources.map((src) => (
                <li key={src.url}>
                  <a href={src.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-[#0066CC] hover:underline">
                    {src.label} <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                  {print && <span className="block text-xs text-slate-500 break-all">{src.url}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
        {print ? (
          <div className="border-t border-slate-200 pt-4 text-xs text-slate-500 leading-relaxed">
            <p className="font-semibold text-slate-700">Important notice</p>
            <p className="mt-1">
              Last reviewed {c.reviewed}. This guide is provided for general information only. It is not tax, legal or accounting advice and does not replace advice from a qualified professional. Rules, deadlines and formats change. Taxera Technologies GmbH does not accept liability for decisions taken on the basis of this guide. Please do your own research and check the official sources listed above before you act.
            </p>
            <p className="mt-3">
              The latest version of this guide is always at{' '}
              <a href={`https://taxera.tech${path}/${c.slug}`} className="text-[#0066CC]">taxera.tech{path}/{c.slug}</a>
              {' '}| All {s.name} countries:{' '}
              <a href={`https://taxera.tech${path}`} className="text-[#0066CC]">taxera.tech{path}</a>
              {' '}| <a href="https://taxera.tech" className="text-[#0066CC]">taxera.tech</a>
            </p>
          </div>
        ) : (
          <>
            <div className="mb-10 rounded-2xl border border-slate-200 bg-slate-50 p-6 flex flex-wrap items-center justify-between gap-4" data-testid="sol-country-download-bottom">
              <div>
                <h2 className="text-lg font-bold text-slate-900">{ui.downloadTitle}</h2>
                <p className="text-sm text-slate-600">{fill(ui.downloadText, vars)}</p>
              </div>
              <SolutionGuideDownload s={s} c={c} />
            </div>
            <h2 className="text-lg font-bold text-slate-900 mb-3">{fill(ui.moreHeading, vars)}</h2>
            <div className="flex flex-wrap gap-3">
              <Link to={path} className="text-sm font-semibold px-4 py-2 rounded-full border border-slate-200 hover:border-[#0066CC] hover:text-[#0066CC] transition-colors" data-testid="sol-country-all">
                {fill(ui.allLabel, vars)}
              </Link>
              {others.map((o) => (
                <Link
                  key={o.slug}
                  to={`${path}/${o.slug}`}
                  className="text-sm font-semibold px-4 py-2 rounded-full border border-slate-200 hover:border-[#0066CC] hover:text-[#0066CC] transition-colors"
                  data-testid={`sol-country-other-${o.slug}`}
                >
                  {fill(ui.otherLabel, { name: o.name, product: s.name })}
                </Link>
              ))}
              {ui.trackerPath && (
                <Link to={ui.trackerPath} className="text-sm font-semibold px-4 py-2 rounded-full border border-slate-200 hover:border-[#0066CC] hover:text-[#0066CC] transition-colors" data-testid="sol-country-tracker-all">
                  All countries in the Tax Tracker
                </Link>
              )}
            </div>
          </>
        )}
      </section>
    </div>
  );
};
// Loads the full content of a country, or null while loading, or false if there is none.
export const useCountryDetail = (productSlug, countrySlug) => {
  const [state, setState] = useState({ key: '', detail: null, done: false });
  const key = `${productSlug}/${countrySlug}`;
  useEffect(() => {
    let alive = true;
    loadCountryDetail(productSlug, countrySlug)
      .then((d) => alive && setState({ key, detail: d, done: true }))
      .catch(() => alive && setState({ key, detail: null, done: true }));
    return () => {
      alive = false;
    };
  }, [productSlug, countrySlug, key]);
  if (state.key !== key) return { detail: null, done: false };
  return { detail: state.detail, done: state.done };
};
const SolutionCountryPage = () => {
  const { product, country } = useParams();
  const s = getSolution(product);
  const meta = s ? getSolutionCountry(product, country) : null;
  const { detail, done } = useCountryDetail(product, country);
  const c = detail ? { ...meta, ...detail } : null;
  // Page-level structured data (FAQ + breadcrumb). Removed when leaving the page.
  useEffect(() => {
    if (!c) return undefined;
    const faqs = buildFaqs(s, c);
    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://taxera.tech/' },
          { '@type': 'ListItem', position: 2, name: s.name, item: `https://taxera.tech${solutionPath(s.slug)}` },
          { '@type': 'ListItem', position: 3, name: c.name, item: `https://taxera.tech${solutionPath(s.slug)}/${c.slug}` },
        ],
      },
    ];
    const nodes = schemas.map((schema, i) => {
      const el = document.createElement('script');
      el.type = 'application/ld+json';
      el.setAttribute('data-page-jsonld', `sol-country-${i}`);
      el.textContent = JSON.stringify(schema);
      document.head.appendChild(el);
      return el;
    });
    return () => nodes.forEach((n) => n.remove());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c && c.slug, product]);
  if (!s || !meta) return <NotFoundPage />;
  if (done && !detail) return <NotFoundPage />;
  if (!c) {
    return (
      <PageLayout>
        <div className="max-w-6xl mx-auto px-6 pt-16 pb-40" data-testid="sol-country-loading">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 leading-[1.1] max-w-4xl">{meta.name}</h1>
        </div>
      </PageLayout>
    );
  }
  return (
    <PageLayout>
      <SolutionCountryContent s={s} c={c} />
    </PageLayout>
  );
};
export default SolutionCountryPage;
